'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import ProductCard from '@/components/ProductCard';
import SoldOutBadge from '@/components/SoldOutBadge';
import { GAME_CATEGORIES, ALL_PRODUCTS, getProductsByGame, getCategoryBySlug, isPurchasable } from '@/lib/products';
import { useI18n } from '@/i18n/LocaleProvider';
import { localizeCategory } from '@/i18n/products';

export default function CategoryPageContent() {
  const searchParams = useSearchParams();
  const gameSlug = searchParams.get('game');
  const sectionRef = useRef<HTMLDivElement>(null);
  const { locale, t } = useI18n();
  const categories = GAME_CATEGORIES.map(cat => localizeCategory(cat, locale));

  const currentCategory = localizeCategory(gameSlug ? getCategoryBySlug(gameSlug) : null, locale);
  const products = gameSlug ? getProductsByGame(gameSlug) : ALL_PRODUCTS;
  const allSoldOut = products.length > 0 && !products.some(isPurchasable);
  const displayTitle = currentCategory?.name ?? t.category.allProducts;

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    const children = section.querySelectorAll('.stagger-child');
    children.forEach((child, i) => {
      (child as HTMLElement).style.transitionDelay = `${i * 70}ms`;
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, [gameSlug]);

  return (
    <main className="pt-32 pb-20">
      {/* Category Hero */}
      <div className="relative overflow-hidden">
        {currentCategory && (
          <div className="absolute inset-0 pointer-events-none">
            <AppImage
              src={currentCategory.image}
              alt={currentCategory.imageAlt}
              fill
              sizes="100vw"
              className="object-cover opacity-10"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/60 to-background" />
          </div>
        )}
        {/* Ambient blob */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 blob-primary opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-foreground transition-colors">{t.nav.home}</Link>
            <span>/</span>
            <Link href="/category" className="hover:text-foreground transition-colors">{t.nav.categories}</Link>
            {currentCategory && (
              <>
                <span>/</span>
                <span className="text-foreground font-medium">{currentCategory.shortName}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              {currentCategory && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: currentCategory.color }} />
                  <span className="text-xs font-bold tracking-widest uppercase" style={{ color: currentCategory.color }}>
                    {t.common.productCount(currentCategory.productCount)}
                  </span>
                </div>
              )}
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground mb-2">
                {displayTitle}
              </h1>
              {currentCategory && (
                <p className="text-muted-foreground text-base max-w-xl">{currentCategory.description}</p>
              )}
            </div>
            {allSoldOut && (
              <div className="flex items-center gap-2 glass-card rounded-full px-4 py-2 flex-shrink-0">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-semibold text-muted-foreground">{t.category.allSoldOut}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Game filter tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-10 scrollbar-hide" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <Link
            href="/category"
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
              !gameSlug
                ? 'bg-primary text-primary-foreground'
                : 'glass-card text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.nav.allTcgs}
          </Link>
          {categories.map(cat => (
            <Link
              key={cat.id}
              href={`/category?game=${cat.slug}`}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                gameSlug === cat.slug
                  ? 'text-background font-bold' :'glass-card text-muted-foreground hover:text-foreground'
              }`}
              style={gameSlug === cat.slug ? { backgroundColor: cat.color } : {}}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: gameSlug === cat.slug ? 'rgba(0,0,0,0.4)' : cat.color }} />
              {cat.shortName}
            </Link>
          ))}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {t.category.showing(products.length, currentCategory?.name)}
          </p>
          {allSoldOut && <SoldOutBadge size="sm" />}
        </div>

        {/* Products grid */}
        {products.length === 0 ? (
          <div className="text-center py-20 glass-card rounded-2xl">
            <p className="text-2xl font-bold text-foreground mb-2">{t.common.noProducts}</p>
            <p className="text-muted-foreground mb-6">{t.category.checkBack}</p>
            <Link href="/category" className="text-primary hover:text-accent transition-colors font-semibold">
              {t.common.browseAllTcgs} →
            </Link>
          </div>
        ) : (
          <div ref={sectionRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map(product => (
              <div key={product.id} className="stagger-child">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        {/* Other categories CTA */}
        {gameSlug && (
          <div className="mt-16 glass-card rounded-2xl p-8 border-top-glow border-top-glow-gold">
            <h3 className="text-xl font-bold text-foreground mb-4">{t.category.exploreOther}</h3>
            <div className="flex flex-wrap gap-3">
              {categories.filter(c => c.slug !== gameSlug).map(cat => (
                <Link
                  key={cat.id}
                  href={`/category?game=${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm font-semibold text-muted-foreground hover:text-foreground transition-all hover:border-primary/40 border border-border"
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                  {cat.shortName}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}